import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Plus,
  Search,
  Users,
  Edit,
  Trash2,
  Mail,
  User,
  Loader,
  AlertCircle,
  X,
  Eye,
  EyeOff,
  Key,
  Phone,
  Check,
  ChevronDown,
  Globe,
} from "lucide-react";
import {
  getAllAgents,
  createAgent,
  updateAgent,
  deleteAgent,
  updateAgentPassword,
} from "../apis/agent";
import { getUserWhatsappPhoneNumbers } from "../apis/whatsappAccount";
import { toast } from "react-toastify";

// ─── Config ──────────────────────────────────────────────────────────────────

/**
 * Sections that are phone-scoped (each phone can have these individually).
 * Non-phone sections only have phone_id=null.
 */
const PHONE_SECTIONS = [
  { value: "templates", label: "Templates" },
  { value: "campaigns", label: "Campaigns" },
  { value: "flows", label: "Flows" },
  { value: "contacts_management", label: "Contacts" },
  { value: "downloads", label: "Downloads" },
  { value: "phone_numbers", label: "Phone Numbers" },
];

const LIVECHAT_OPTIONS = [
  { value: "none", label: "No Access" },
  { value: "livechats.own", label: "Own Chats Only" },
  { value: "livechats.all", label: "All Chats" },
];

const NON_PHONE_SECTIONS = [
  { value: "broadcasting", label: "Broadcasting" },
  { value: "dashboard", label: "Dashboard" },
  { value: "profile", label: "Profile" },
  { value: "settings", label: "Settings" },
  { value: "agents", label: "Agents" },
];

// ─── Helpers: convert between flat rows and UI state ─────────────────────────

/**
 * UI state shape: {
 *   nonPhoneSections: Set<string>,                     // e.g. "dashboard", "downloads"
 *   phonePerms: Map<phoneId, { sections: Set<string>, livechat: 'none'|'livechats.own'|'livechats.all' }>
 *   allPhonesPerms: { sections: Set<string>, livechat: 'none'|'livechats.own'|'livechats.all' }
 * }
 */

const VIRTUAL_ALL = "*"; // represents "All Phones"

function rowsToState(rows) {
  const nonPhoneSections = new Set();
  const phonePerms = new Map(); // phoneId → { sections: Set, livechat }
  const allPhonesPerms = { sections: new Set(), livechat: "none" };

  for (const r of rows) {
    const { section, phone_id } = r;

    // Non-phone section
    if (phone_id === null || phone_id === undefined) {
      nonPhoneSections.add(section);
      continue;
    }

    const isAllPhones = phone_id === VIRTUAL_ALL;
    const target = isAllPhones ? allPhonesPerms : getOrCreate(phonePerms, phone_id);

    if (section === "livechats.all") {
      target.livechat = "livechats.all";
    } else if (section === "livechats.own") {
      if (target.livechat !== "livechats.all") target.livechat = "livechats.own";
    } else {
      target.sections.add(section);
    }
  }

  return { nonPhoneSections, phonePerms, allPhonesPerms };
}

function stateToRows(state) {
  const rows = [];

  // Non-phone sections
  for (const sec of state.nonPhoneSections) {
    rows.push({ section: sec, phone_id: null });
  }

  // All-phones
  for (const sec of state.allPhonesPerms.sections) {
    rows.push({ section: sec, phone_id: VIRTUAL_ALL });
  }
  if (state.allPhonesPerms.livechat !== "none") {
    rows.push({ section: state.allPhonesPerms.livechat, phone_id: VIRTUAL_ALL });
  }

  // Per-phone
  for (const [phoneId, data] of state.phonePerms) {
    for (const sec of data.sections) {
      rows.push({ section: sec, phone_id: phoneId });
    }
    if (data.livechat !== "none") {
      rows.push({ section: data.livechat, phone_id: phoneId });
    }
  }

  return rows;
}

function getOrCreate(map, key) {
  if (!map.has(key)) map.set(key, { sections: new Set(), livechat: "none" });
  return map.get(key);
}

// ─── Sub-components ──────────────────────────────────────────────────────────

/** Phone selector dropdown for picking which phone to configure */
const PhonePickerDropdown = ({ phones, selectedPhoneId, onSelect, showAllPhones = true }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const selectedLabel = useMemo(() => {
    if (selectedPhoneId === VIRTUAL_ALL) return "All Phones";
    const ph = phones.find((p) => p.phone_id === selectedPhoneId);
    return ph ? `${ph.verified_name || "Account"} (${ph.phone_number})` : "Select a phone...";
  }, [selectedPhoneId, phones]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm border border-gray-200 rounded-xl hover:border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
      >
        <div className="flex items-center gap-2 truncate">
          {selectedPhoneId === VIRTUAL_ALL ? (
            <Globe size={15} className="text-gray-500 flex-shrink-0" />
          ) : (
            <Phone size={15} className="text-gray-500 flex-shrink-0" />
          )}
          <span className="text-gray-800 truncate font-medium">{selectedLabel}</span>
        </div>
        <ChevronDown size={14} className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-50 left-0 top-full mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-lg py-1">
          {showAllPhones && (
            <button
              type="button"
              onClick={() => { onSelect(VIRTUAL_ALL); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${
                selectedPhoneId === VIRTUAL_ALL ? "bg-gray-100 font-semibold" : "hover:bg-gray-50"
              }`}
            >
              <Globe size={14} className="text-gray-500" />
              <span>All Phones</span>
            </button>
          )}
          {phones.map((p) => (
            <button
              key={p.phone_id}
              type="button"
              onClick={() => { onSelect(p.phone_id); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${
                selectedPhoneId === p.phone_id ? "bg-gray-100 font-semibold" : "hover:bg-gray-50"
              }`}
            >
              <Phone size={14} className="text-gray-400" />
              <span className="truncate">{p.verified_name || "Account"} <span className="text-gray-500">({p.phone_number})</span></span>
            </button>
          ))}
          {phones.length === 0 && (
            <p className="px-3 py-2 text-xs text-gray-400">No phones available</p>
          )}
        </div>
      )}
    </div>
  );
};

/** Per-phone section toggles: templates, campaigns, flows, livechat */
const PhoneSectionToggles = ({ phoneId, state, setState }) => {
  const isAll = phoneId === VIRTUAL_ALL;
  const data = isAll ? state.allPhonesPerms : state.phonePerms.get(phoneId);
  const sections = data?.sections || new Set();
  const livechat = data?.livechat || "none";

  const update = (newSections, newLivechat) => {
    const next = { ...state };
    const entry = { sections: newSections, livechat: newLivechat };
    if (isAll) {
      next.allPhonesPerms = entry;
    } else {
      next.phonePerms = new Map(state.phonePerms);
      next.phonePerms.set(phoneId, entry);
    }
    setState(next);
  };

  const toggleSection = (sec) => {
    const s = new Set(sections);
    if (s.has(sec)) s.delete(sec);
    else s.add(sec);
    update(s, livechat);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
        Sections for {isAll ? "All Phones" : "this phone"}
      </p>
      <div className="grid grid-cols-3 gap-2">
        {PHONE_SECTIONS.map((sec) => {
          const on = sections.has(sec.value);
          return (
            <button
              key={sec.value}
              type="button"
              onClick={() => toggleSection(sec.value)}
              className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                on
                  ? "border-gray-900 bg-gray-900 text-white"
                  : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
              }`}
            >
              {sec.label}
            </button>
          );
        })}
      </div>
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Live Chat Access
        </p>
        <div className="flex gap-2">
          {LIVECHAT_OPTIONS.map((opt) => {
            const active = livechat === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => update(sections, opt.value)}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors flex-1 ${
                  active
                    ? opt.value === "livechats.all"
                      ? "border-emerald-600 bg-emerald-600 text-white"
                      : opt.value === "livechats.own"
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-gray-400 bg-gray-100 text-gray-700"
                    : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const Agents = () => {
  const [agents, setAgents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Platform phones
  const [platformPhones, setPlatformPhones] = useState([]);
  const [phonesLoading, setPhonesLoading] = useState(false);

  // Form
  const [formData, setFormData] = useState({ name: "", email: "", password: "" });
  const [permState, setPermState] = useState({
    nonPhoneSections: new Set(),
    phonePerms: new Map(),
    allPhonesPerms: { sections: new Set(), livechat: "none" },
  });
  const [selectedPhoneForConfig, setSelectedPhoneForConfig] = useState(VIRTUAL_ALL);

  const [passwordData, setPasswordData] = useState({ newPassword: "", confirmPassword: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { fetchAgents(); }, []);

  const fetchPlatformPhones = async () => {
    if (platformPhones.length > 0) return;
    setPhonesLoading(true);
    try {
      const res = await getUserWhatsappPhoneNumbers();
      if (res.data.success) setPlatformPhones(res.data.data || []);
    } catch (err) {
      console.error("Error fetching platform phones:", err);
    } finally {
      setPhonesLoading(false);
    }
  };

  const fetchAgents = async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await getAllAgents();
      if (response.data.success) setAgents(response.data.agents || []);
    } catch (err) {
      console.error("Error fetching agents:", err);
      setError(err.response?.data?.error || "Failed to load agents");
      toast.error(err.response?.data?.error || "Failed to load agents");
    } finally {
      setIsLoading(false);
    }
  };

  // ── Build rows from state and send to API ──

  const buildPermissions = () => stateToRows(permState);

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await createAgent({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        permissions: buildPermissions(),
      });
      if (response.data.success) {
        toast.success("Agent created successfully");
        setShowCreateModal(false);
        resetForm();
        fetchAgents();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to create agent");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditAgent = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await updateAgent(selectedAgent.id, {
        name: formData.name,
        email: formData.email,
        permissions: buildPermissions(),
      });
      if (response.data.success) {
        toast.success("Agent updated successfully");
        setShowEditModal(false);
        setSelectedAgent(null);
        resetForm();
        fetchAgents();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update agent");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) { toast.error("Passwords do not match"); return; }
    if (passwordData.newPassword.length < 6) { toast.error("Password must be at least 6 characters long"); return; }
    setIsSubmitting(true);
    try {
      const response = await updateAgentPassword(selectedAgent.id, passwordData.newPassword);
      if (response.data.success) {
        toast.success("Password updated successfully");
        setShowPasswordModal(false);
        setSelectedAgent(null);
        setPasswordData({ newPassword: "", confirmPassword: "" });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update password");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAgent = async () => {
    setIsSubmitting(true);
    try {
      const response = await deleteAgent(selectedAgent.id);
      if (response.data.success) {
        toast.success("Agent deleted successfully");
        setShowDeleteModal(false);
        setSelectedAgent(null);
        fetchAgents();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete agent");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: "", email: "", password: "" });
    setPermState({
      nonPhoneSections: new Set(),
      phonePerms: new Map(),
      allPhonesPerms: { sections: new Set(), livechat: "none" },
    });
    setSelectedPhoneForConfig(VIRTUAL_ALL);
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
    fetchPlatformPhones();
  };

  const openEditModal = (agent) => {
    setSelectedAgent(agent);
    setFormData({ name: agent.name, email: agent.email, password: "" });
    setPermState(rowsToState(agent.permissions || []));
    setSelectedPhoneForConfig(VIRTUAL_ALL);
    setShowEditModal(true);
    fetchPlatformPhones();
  };

  const openPasswordModal = (agent) => {
    setSelectedAgent(agent);
    setPasswordData({ newPassword: "", confirmPassword: "" });
    setShowPasswordModal(true);
  };

  const openDeleteModal = (agent) => {
    setSelectedAgent(agent);
    setShowDeleteModal(true);
  };

  const filteredAgents = agents.filter(
    (agent) =>
      agent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.id?.toString().includes(searchTerm)
  );

  // ── Non-phone section toggles ──

  const toggleNonPhoneSection = (sec) => {
    const s = new Set(permState.nonPhoneSections);
    if (s.has(sec)) s.delete(sec);
    else s.add(sec);
    setPermState({ ...permState, nonPhoneSections: s });
  };

  // ── Render phone perms for selected phone ──

  const currentPhoneData = selectedPhoneForConfig === VIRTUAL_ALL
    ? permState.allPhonesPerms
    : permState.phonePerms.get(selectedPhoneForConfig);

  const hasAnyPhonePerms = (currentPhoneData?.sections?.size > 0) || (currentPhoneData?.livechat && currentPhoneData.livechat !== "none");

  // ── Permission Summary for Agent Card ──

  const renderPermSummary = (agent) => {
    const perms = agent.permissions || [];
    if (perms.length === 0) return <p className="text-xs text-gray-400 italic">No permissions assigned</p>;

    const state = rowsToState(perms);
    const badges = [];

    // Non-phone
    for (const sec of state.nonPhoneSections) {
      const label = NON_PHONE_SECTIONS.find((s) => s.value === sec)?.label || sec;
      badges.push(<span key={`np_${sec}`} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">{label}</span>);
    }

    // All-phones
    const apSecs = [...state.allPhonesPerms.sections];
    const apLc = state.allPhonesPerms.livechat;
    if (apSecs.length > 0 || apLc !== "none") {
      const parts = apSecs.map((s) => PHONE_SECTIONS.find((x) => x.value === s)?.label || s);
      if (apLc !== "none") parts.push(apLc === "livechats.all" ? "LC:All" : "LC:Own");
      badges.push(
        <span key="all_phones" className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-800 rounded">
          All Phones: {parts.join(", ")}
        </span>
      );
    }

    // Per-phone
    for (const [phoneId, data] of state.phonePerms) {
      const parts = [...data.sections].map((s) => PHONE_SECTIONS.find((x) => x.value === s)?.label || s);
      if (data.livechat !== "none") parts.push(data.livechat === "livechats.all" ? "LC:All" : "LC:Own");
      if (parts.length === 0) continue;
      badges.push(
        <span key={`ph_${phoneId}`} className="px-2 py-0.5 text-xs bg-blue-50 text-blue-800 rounded">
          {phoneId.slice(-4)}: {parts.join(", ")}
        </span>
      );
    }

    return <div className="flex flex-wrap gap-1">{badges}</div>;
  };

  // ── Permissions Form (phone-first) ──

  const renderPermissionsForm = () => (
    <div className="space-y-4">
      {/* Non-phone sections */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">General Sections</label>
        <div className="grid grid-cols-2 gap-2">
          {NON_PHONE_SECTIONS.map((sec) => {
            const on = permState.nonPhoneSections.has(sec.value);
            return (
              <button
                key={sec.value}
                type="button"
                onClick={() => toggleNonPhoneSection(sec.value)}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors text-left ${
                  on
                    ? "border-gray-900 bg-gray-900 text-white"
                    : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                }`}
              >
                {sec.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Phone-scoped permissions */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Phone-Based Permissions
        </label>
        <p className="text-xs text-gray-500 mb-3">
          Select a phone number below and configure which sections the agent can access for that phone.
          Use "All Phones" to grant access across every phone.
        </p>

        {phonesLoading ? (
          <div className="flex items-center gap-2 text-gray-400 text-sm py-4">
            <Loader size={16} className="animate-spin" /> Loading phones...
          </div>
        ) : (
          <>
            <PhonePickerDropdown
              phones={platformPhones}
              selectedPhoneId={selectedPhoneForConfig}
              onSelect={setSelectedPhoneForConfig}
            />

            {selectedPhoneForConfig && (
              <div className="mt-3 border border-gray-200 rounded-xl p-4 bg-gray-50/50">
                <PhoneSectionToggles
                  phoneId={selectedPhoneForConfig}
                  state={permState}
                  setState={setPermState}
                />
              </div>
            )}

            {/* Quick summary of configured phones */}
            <div className="mt-3 space-y-1">
              {permState.allPhonesPerms.sections.size > 0 || permState.allPhonesPerms.livechat !== "none" ? (
                <div className="flex items-center gap-2 text-xs text-emerald-700">
                  <Globe size={12} />
                  <span>
                    All Phones: {[...permState.allPhonesPerms.sections].join(", ")}
                    {permState.allPhonesPerms.livechat !== "none" && ` + ${permState.allPhonesPerms.livechat}`}
                  </span>
                </div>
              ) : null}
              {[...permState.phonePerms.entries()].map(([pid, d]) => {
                const parts = [...d.sections];
                if (d.livechat !== "none") parts.push(d.livechat);
                if (parts.length === 0) return null;
                const ph = platformPhones.find((p) => p.phone_id === pid);
                return (
                  <div key={pid} className="flex items-center gap-2 text-xs text-blue-700">
                    <Phone size={12} />
                    <span>{ph?.phone_number || pid.slice(-6)}: {parts.join(", ")}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Agents</h1>
          <p className="text-gray-600 mt-1">Manage your agents and their access</p>
        </div>
        <button
          onClick={openCreateModal}
          className="ping-btn ping-btn-primary"
        >
          <Plus size={20} />
          <span>Add Agent</span>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search agents by name, email, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent"
          />
        </div>
      </div>

      {/* Agent List */}
      {filteredAgents.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <Users className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-600 text-lg">
            {searchTerm ? "No agents found matching your search" : "No agents yet"}
          </p>
          {!searchTerm && (
            <button onClick={openCreateModal} className="mt-4 text-[#17181b] hover:underline font-semibold">
              Add your first agent
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredAgents.map((agent) => (
            <div key={agent.id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-[#EEFA94] rounded-lg flex-shrink-0">
                      <User size={20} className="text-gray-900" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">{agent.name}</h3>
                      <p className="text-sm text-gray-500">ID: {agent.id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 mt-2">
                    <Mail size={16} />
                    <span className="text-sm">{agent.email}</span>
                  </div>
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-gray-700 mb-1">Permissions:</p>
                    {renderPermSummary(agent)}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Created: {new Date(agent.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => openEditModal(agent)} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" title="Edit Agent">
                    <Edit size={18} />
                  </button>
                  <button onClick={() => openPasswordModal(agent)} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" title="Change Password">
                    <Key size={18} />
                  </button>
                  <button onClick={() => openDeleteModal(agent)} className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors" title="Delete Agent">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Agent Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Add New Agent</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
            </div>
            <form onSubmit={handleCreateAgent} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} required value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="w-full px-4 py-2 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
              </div>

              {renderPermissionsForm()}

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">Cancel</button>
                <button type="submit" disabled={isSubmitting} className="flex-1 ping-btn ping-btn-primary disabled:opacity-50">
                  {isSubmitting ? "Creating..." : "Create Agent"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Agent Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Edit Agent</h2>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
            </div>
            <form onSubmit={handleEditAgent} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300" />
              </div>

              {renderPermissionsForm()}

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowEditModal(false)} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">Cancel</button>
                <button type="submit" disabled={isSubmitting} className="flex-1 ping-btn ping-btn-primary disabled:opacity-50">
                  {isSubmitting ? "Updating..." : "Update Agent"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Change Password</h2>
              <button onClick={() => setShowPasswordModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
            </div>
            <p className="text-sm text-gray-600 mb-4">Update password for <strong>{selectedAgent?.name}</strong></p>
            <form onSubmit={handleUpdatePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">New Password</label>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} required value={passwordData.newPassword} onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })} className="w-full px-4 py-2 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm Password</label>
                <input type={showPassword ? "text" : "password"} required value={passwordData.confirmPassword} onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowPasswordModal(false)} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">Cancel</button>
                <button type="submit" disabled={isSubmitting} className="flex-1 ping-btn ping-btn-primary disabled:opacity-50">
                  {isSubmitting ? "Updating..." : "Update Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Delete Agent</h2>
              <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete agent <strong>{selectedAgent?.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={handleDeleteAgent} disabled={isSubmitting} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50">
                {isSubmitting ? "Deleting..." : "Delete Agent"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Agents;
